import { useCallback, useMemo, useState } from 'react';
/**
 * Resolve the V2 onboarding mode for the user clicking "Create vendor account".
 *
 * `isClient` reads the active worksAt's party type. Clicking the CTA calls
 * setWorksAt() and may swap the JWT, which can flip data.type from Customer to
 * Vendor before HomePage's effect re-runs — losing the `isClient` signal. Using
 * `hasCustomerWorkspace` (a property of the user's workspace list, not their
 * active context) restores it: a user who has at least one Customer workspace
 * is — by definition — coming from a client surface, regardless of which
 * business they are momentarily switched to.
 */ export const deriveVobMode = (param)=>{
    let { sp_onboarding_phase_1, isClient, hasCustomerWorkspace, hasCompletedVendorWorkspace } = param;
    if (!sp_onboarding_phase_1) return 'default';
    if (isClient || hasCustomerWorkspace) return 'clientToVendor';
    if (hasCompletedVendorWorkspace) return 'additionalAccount';
    return 'default';
};
const DEFAULT_STEPS = [
    'intro',
    'businessDetailsA',
    'businessDetailsB',
    'intent',
    'integrations',
    'qboXeroIntegration',
    'inviteTeam',
    'businessWebsite',
    'brand',
    'waiting',
    'closing'
];
const ADDITIONAL_ACCOUNT_STEPS = DEFAULT_STEPS.filter((step)=>step !== 'intro' && step !== 'intent' && step !== 'waiting');
// Client-to-vendor is a deliberately minimal flow per Figma frame 5152-18068. Clients clicking
// "Create vendor account" already have the surrounding business / contact context implicitly,
// so we skip business-details, intent, integrations, invite-team, and website steps and only
// keep intro (personalized welcome), brand, secureLogin (NEW), waiting, and closing.
const CLIENT_TO_VENDOR_STEPS = [
    'intro',
    'brand',
    'secureLogin',
    'waiting',
    'closing'
];
const STEPS_BY_MODE = {
    default: DEFAULT_STEPS,
    additionalAccount: ADDITIONAL_ACCOUNT_STEPS,
    clientToVendor: CLIENT_TO_VENDOR_STEPS
};
const DATA_COLLECTION_STEPS = new Set([
    'businessDetailsA',
    'businessDetailsB',
    'intent',
    'inviteTeam',
    'businessWebsite',
    'brand',
    'secureLogin',
    'integrations',
    'qboXeroIntegration'
]);
const SKIPPABLE_STEPS = new Set([
    'intent',
    'integrations',
    'inviteTeam',
    'businessWebsite',
    'brand'
]);
const CREDITS_STORAGE_KEY = 'anchor.vendorOnboarding.creditsAwarded';
export const readPersistedCreditsAwarded = ()=>{
    try {
        return sessionStorage.getItem(CREDITS_STORAGE_KEY) === 'true';
    } catch (unused) {
        return false;
    }
};
export const persistCreditsAwarded = (awarded)=>{
    try {
        if (awarded) {
            sessionStorage.setItem(CREDITS_STORAGE_KEY, 'true');
        } else {
            sessionStorage.removeItem(CREDITS_STORAGE_KEY);
        }
    } catch (unused) {
    // sessionStorage may be unavailable — fail silently
    }
};
export const clearPersistedOnboardingProgress = ()=>{
    try {
        sessionStorage.removeItem(CREDITS_STORAGE_KEY);
    } catch (unused) {
    // sessionStorage may be unavailable — fail silently
    }
};
export const useOnboardingFlow = (options)=>{
    var _ref;
    const mode = (_ref = options === null || options === void 0 ? void 0 : options.mode) !== null && _ref !== void 0 ? _ref : 'default';
    const baseSteps = STEPS_BY_MODE[mode];
    const activeSteps = useMemo(()=>{
        return baseSteps.filter((step)=>{
            if ((options === null || options === void 0 ? void 0 : options.skipInviteTeam) && step === 'inviteTeam') {
                return false;
            }
            if (((options === null || options === void 0 ? void 0 : options.skipQboXero) || (options === null || options === void 0 ? void 0 : options.isMobile)) && step === 'qboXeroIntegration') {
                return false;
            }
            return true;
        });
    }, [
        baseSteps,
        options === null || options === void 0 ? void 0 : options.skipInviteTeam,
        options === null || options === void 0 ? void 0 : options.skipQboXero,
        options === null || options === void 0 ? void 0 : options.isMobile
    ]);
    const [stepIndex, setStepIndex] = useState(()=>{
        if (!readPersistedCreditsAwarded()) return 0;
        const idx = activeSteps.indexOf('businessDetailsA');
        return idx >= 0 ? idx : 0;
    });
    const clampedIndex = Math.min(stepIndex, activeSteps.length - 1);
    const currentStep = activeSteps[clampedIndex];
    // Progress denominator: count every data-collection step in this mode's base list, regardless
    // of which ones are currently filtered out for navigation. inviteTeam (skipInviteTeam) and
    // qboXeroIntegration (skipQboXero) still occupy a slot on the bar so it does not jump or
    // shrink mid-flow.
    const progressSteps = useMemo(()=>baseSteps.filter((step)=>DATA_COLLECTION_STEPS.has(step)), [
        baseSteps
    ]);
    const totalDataCollectionSteps = progressSteps.length;
    const dataCollectionIndex = progressSteps.indexOf(currentStep);
    const isDataCollectionStep = dataCollectionIndex >= 0;
    const isSkippableStep = SKIPPABLE_STEPS.has(currentStep);
    const prevStep = clampedIndex > 0 ? activeSteps[clampedIndex - 1] : null;
    const isBackDisabled = clampedIndex === 0 || prevStep === 'businessDetailsB';
    const progress = isDataCollectionStep ? (dataCollectionIndex + 1) / (totalDataCollectionSteps + 1) * 100 : 0;
    const goNext = useCallback(()=>{
        setStepIndex((prev)=>Math.min(prev + 1, activeSteps.length - 1));
    }, [
        activeSteps.length
    ]);
    const goBack = useCallback(()=>{
        setStepIndex((prev)=>Math.max(prev - 1, 0));
    }, []);
    return {
        currentStep,
        progress,
        isDataCollectionStep,
        isSkippableStep,
        isBackDisabled,
        goNext,
        goBack
    };
};
